/**
 * Scoring. One standardized word = 5 keystrokes, the convention Keybr, Monkeytype
 * and the Bangladesh government recruitment rules all share ("৫টি স্ট্রোক = ১ শব্দ").
 * For Bangla the stroke is a Bijoy keystroke, so Bangla and English speeds compare.
 */

const minutesBetween = (startedAt, finishedAt) => Math.max((finishedAt - startedAt) / 60000, 1 / 600);

/** Gross WPM from a raw keystroke count. */
export const wpmFromStrokes = (strokes, minutes) => (strokes / 5) / minutes;

/**
 * Score a strict (lesson / drill) run. Each unit was typed right in the end — a wrong
 * key is counted but refused — so the length is the units and accuracy is the share
 * of first attempts that hit the right key.
 * @returns {{ wpm, accuracy, rawAccuracy, strokes, correct, errors, seconds, units }}
 */
export function scoreStrict(session) {
    const started = session.startedAt;
    const finished = session.finishedAt ?? session.lastAt ?? started;
    const minutes = started ? minutesBetween(started, finished) : 1 / 600;
    const correct = session.log.filter((e) => e.ok).length;
    const errors = session.log.length - correct;
    // Every unit ends with a correct keystroke, so correct strokes = keystrokes typed right.
    const wpm = wpmFromStrokes(correct, minutes);
    const total = session.log.length || 1;
    return {
        wpm: Math.max(0, wpm),
        accuracy: (correct / total) * 100, // raw: every wrong key counts, even corrected
        rawAccuracy: (correct / total) * 100,
        strokes: correct,
        correct,
        errors,
        seconds: minutes * 60,
        units: session.units.length,
    };
}

const normalise = (s) => String(s || '').normalize('NFC');

/**
 * Score a free (test / exam) run against the reference words, the way a real typing
 * test marks it: a word-level diff, then speed from the keystrokes actually pressed.
 * @returns {{
 *   wpm, netWpm, accuracy, seconds, minutes, keystrokes, correctWords, wrongWords,
 *   missedWords, correctChars, errorRate, passed, words
 * }}
 */
export function scoreFree(session, { target = null } = {}) {
    const started = session.startedAt;
    const finished = session.finishedAt ?? session.lastAt ?? started;
    const minutes = started ? minutesBetween(started, finished) : 1 / 600;
    const reached = session.index + (session.finishedAt ? 1 : 0); // words attempted
    const words = session.words.map((word, i) => {
        const typed = normalise(session.rendered[i] || '');
        const attempted = i < reached && typed.length > 0;
        const correct = attempted && typed === normalise(word);
        return { word, typed, attempted, correct };
    });
    const correctWords = words.filter((w) => w.correct).length;
    const wrongWords = words.filter((w) => w.attempted && !w.correct).length;
    const missedWords = words.filter((w) => !w.attempted).length;
    // Correct characters, spaces between correct words included — the exam's "stroke" count.
    const correctChars = words.reduce((sum, w, i) => sum + (w.correct ? w.word.length + (i > 0 ? 1 : 0) : 0), 0);
    const keystrokes = session.keystrokes;
    const grossWpm = wpmFromStrokes(keystrokes, minutes);
    const netWpm = wpmFromStrokes(correctChars, minutes);
    const attemptedWords = correctWords + wrongWords;
    const errorRate = attemptedWords ? (wrongWords / attemptedWords) * 100 : 0;
    const accuracy = attemptedWords ? (correctWords / attemptedWords) * 100 : 0;
    const passed = target ? netWpm >= target && errorRate <= 5 : null;
    return {
        wpm: Math.max(0, grossWpm),
        netWpm: Math.max(0, netWpm),
        accuracy,
        seconds: minutes * 60,
        minutes,
        keystrokes,
        corrections: session.corrections,
        correctWords,
        wrongWords,
        missedWords,
        correctChars,
        errorRate,
        passed,
        words,
    };
}

/**
 * Stars for a lesson, modelled on TypingClub's published minimums (accuracy over
 * speed, a goal roughly 1.5× the pass speed for full marks). A design proposal,
 * not a copied formula.
 * @returns {{ stars: 0..5, passed: boolean }}
 */
export function starsFor({ wpm, accuracy }, { minWpm = 10, goalWpm = 20 } = {}) {
    if (accuracy < 80 || wpm < minWpm) return { stars: 0, passed: false };
    let stars = 1;
    if (accuracy >= 90 && wpm >= goalWpm * 0.75) stars = 2;
    if (accuracy >= 93 && wpm >= goalWpm) stars = 3;
    if (accuracy >= 96 && wpm >= goalWpm * 1.25) stars = 4;
    if (accuracy >= 98 && wpm >= goalWpm * 1.5) stars = 5;
    return { stars, passed: true };
}

/**
 * Per-key accuracy and mean time, for the heat-map and for finding weak keys.
 * Works off a strict session's keystroke log.
 * @returns {Record<string, { total, errors, accuracy, meanMs }>}
 */
export function perKeyStats(session, layout) {
    const map = {};
    session.log.forEach((entry) => {
        const key = layout.statKey ? layout.statKey(entry.expected || entry.stroke) : null;
        if (!key) return;
        const row = map[key] || (map[key] = { total: 0, errors: 0, timeSum: 0, timeCount: 0 });
        row.total += 1;
        if (!entry.ok) row.errors += 1;
        if (entry.ok && entry.prevT && entry.t > entry.prevT) {
            row.timeSum += entry.t - entry.prevT;
            row.timeCount += 1;
        }
    });
    const out = {};
    Object.entries(map).forEach(([key, row]) => {
        out[key] = {
            total: row.total,
            errors: row.errors,
            accuracy: ((row.total - row.errors) / row.total) * 100,
            meanMs: row.timeCount ? row.timeSum / row.timeCount : null,
        };
    });
    return out;
}

/** WPM sampled per second across a run's keystroke log, for the result chart. */
export function wpmTimeline(log, { strokeAt = (e) => e.t, isStroke = () => true } = {}) {
    const strokes = log.filter(isStroke).map(strokeAt).sort((a, b) => a - b);
    if (strokes.length < 2) return [];
    const start = strokes[0];
    const buckets = [];
    strokes.forEach((t) => {
        const sec = Math.floor((t - start) / 1000);
        buckets[sec] = (buckets[sec] || 0) + 1;
    });
    return Array.from(buckets, (count, sec) => ({ sec, wpm: ((count || 0) / 5) * 60 }));
}
